import React, { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { PortfolioItem } from '../services/types';

interface ExcelUploadProps {
  onImport: (items: PortfolioItem[]) => void;
  existingItems: PortfolioItem[];
}

interface ParsedRow {
  type?: string;
  symbol?: string;
  quantity?: number;
  buyPrice?: number;
  buyDate?: string;
  name?: string;
  [key: string]: any;
}

const ExcelUpload: React.FC<ExcelUploadProps> = ({ onImport, existingItems }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [previewData, setPreviewData] = useState<ParsedRow[]>([]);
  const [showPreview, setShowPreview] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);

  const parseExcelDate = (dateValue: any): string => {
    if (!dateValue) return new Date().toISOString().split('T')[0];
    
    // Eğer zaten string formatındaysa ve geçerli bir tarihse
    if (typeof dateValue === 'string') {
      // DD.MM.YYYY formatını kontrol et
      const dmyMatch = dateValue.match(/^(\d{2})\.(\d{2})\.(\d{4})$/);
      if (dmyMatch) {
        const [, day, month, year] = dmyMatch;
        return `${year}-${month}-${day}`;
      }
      
      // YYYY-MM-DD formatını kontrol et
      const ymdMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (ymdMatch) {
        return dateValue;
      }
      
      // Diğer string formatları için Date objesine çevir
      const parsedDate = new Date(dateValue);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toISOString().split('T')[0];
      }
    }
    
    // Eğer Excel serial number ise (örn: 44731)
    if (typeof dateValue === 'number') {
      // Excel'in epoch'i 1900-01-01, JavaScript'in epoch'i 1970-01-01
      const excelEpoch = new Date(1900, 0, 1);
      const daysOffset = dateValue - 1; // Excel 1'den başlar
      const jsDate = new Date(excelEpoch.getTime() + daysOffset * 24 * 60 * 60 * 1000);
      
      // Excel'in 1900'ü artık yıl sayması hatası için düzeltme
      if (dateValue > 59) {
        jsDate.setDate(jsDate.getDate() - 1);
      }
      
      return jsDate.toISOString().split('T')[0];
    }
    
    // Hiçbiri değilse bugünün tarihini kullan
    return new Date().toISOString().split('T')[0];
  };

  const validateRow = (row: ParsedRow, index: number): { valid: boolean; errors: string[] } => {
    const errors: string[] = [];
    
    if (!row.symbol || row.symbol.trim() === '') {
      errors.push(`Satır ${index + 1}: Sembol boş olamaz`);
    }
    
    if (!row.quantity || isNaN(row.quantity) || row.quantity <= 0) {
      errors.push(`Satır ${index + 1}: Geçerli bir adet giriniz`);
    }
    
    if (!row.buyPrice || isNaN(row.buyPrice) || row.buyPrice <= 0) {
      errors.push(`Satır ${index + 1}: Geçerli bir alış fiyatı giriniz`);
    }
    
    if (!row.type || !['stock', 'currency', 'gold', 'crypto'].includes(row.type)) {
      errors.push(`Satır ${index + 1}: Geçerli bir tür giriniz (stock, currency, gold, crypto)`);
    }
    
    return { valid: errors.length === 0, errors };
  };

  const parseExcelFile = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet) as ParsedRow[];
        
        // Validate data
        const allErrors: string[] = [];
        const validatedData = jsonData.map((row, index) => {
          const { valid, errors } = validateRow(row, index);
          if (!valid) {
            allErrors.push(...errors);
          }
          return row;
        });
        
        setPreviewData(validatedData);
        setErrors(allErrors);
        setShowPreview(true);
        
        console.log('📊 Excel data parsed:', validatedData);
      } catch (error) {
        console.error('❌ Excel parsing error:', error);
        alert('Excel dosyası okunurken hata oluştu. Lütfen dosya formatını kontrol edin.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        parseExcelFile(file);
      } else {
        alert('Lütfen .xlsx veya .xls formatında bir dosya yükleyin.');
      }
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
        parseExcelFile(file);
      } else {
        alert('Lütfen .xlsx veya .xls formatında bir dosya yükleyin.');
      }
    }
  }, [parseExcelFile]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleConfirmImport = () => {
    if (errors.length > 0) {
      if (!window.confirm('Hatalar var! Yine de geçerli satırları içe aktarmak istiyor musunuz?')) {
        return;
      }
    }

    const portfolioItems: PortfolioItem[] = previewData
      .filter((row, index) => {
        const { valid } = validateRow(row, index);
        return valid;
      })
      .map(row => {
        const quantity = Number(row.quantity) || 0;
        const buyPrice = Number(row.buyPrice) || 0;
        const usdRate = row.usdRate ? Number(row.usdRate) : 0;
        
        return {
          id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
          type: (row.type || 'stock') as 'stock' | 'currency' | 'gold' | 'crypto',
          symbol: (row.symbol || '').toUpperCase().trim(),
          name: row.name || row.symbol || '',
          quantity: quantity,
          buyPrice: buyPrice,
          currentPrice: 0,
          totalValue: 0,
          profit: 0,
          profitPercent: 0,
          buyDate: parseExcelDate(row.buyDate), // 🔧 Tarih parsing fonksiyonunu kullan
          usdRate: usdRate,
          profitUSD: 0,
          profitPercentUSD: 0,
          remainingQuantity: quantity
        };
      });

    onImport(portfolioItems);
    setShowPreview(false);
    setPreviewData([]);
    setErrors([]);
    
    alert(`✅ ${portfolioItems.length} adet varlık başarıyla içe aktarıldı!`);
  };

  const downloadTemplate = () => {
    const template = [
      {
        type: 'stock',
        symbol: 'THYAO',
        quantity: 100,
        buyPrice: 15.50,
        buyDate: '2024-01-15',
        usdRate: 0
      },
      {
        type: 'currency',
        symbol: 'USD',
        quantity: 1000,
        buyPrice: 30.00,
        buyDate: '2024-01-15',
        usdRate: 0
      },
      {
        type: 'gold',
        symbol: 'GAUTRY',
        quantity: 10,
        buyPrice: 1800.00,
        buyDate: '2024-01-15',
        usdRate: 0
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Portföy Template');
    XLSX.writeFile(wb, 'portfoy_template.xlsx');
    
    console.log('📥 Template downloaded');
  };

  return (
    <div className="excel-upload-container">
      <div className="excel-upload-header">
        <h3>📊 Excel ile Toplu Ekleme</h3>
        <button 
          onClick={downloadTemplate}
          className="template-button"
        >
          📥 Örnek Şablon İndir
        </button>
      </div>

      <div 
        className={`excel-dropzone ${isDragging ? 'dragging' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        <div className="excel-dropzone-content">
          <div className="excel-icon">📁</div>
          <p className="excel-text">
            <strong>Excel dosyasını sürükleyin</strong> veya 
            <label className="excel-file-label">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileUpload}
                className="excel-file-input"
              />
              <span className="excel-link">buraya tıklayın</span>
            </label>
          </p>
          <p className="excel-hint">
            Desteklenen formatlar: .xlsx, .xls
          </p>
        </div>
      </div>

      <div className="excel-instructions">
        <h4>📋 Excel Formatı:</h4>
        <table className="excel-format-table">
          <thead>
            <tr>
              <th>Sütun</th>
              <th>Açıklama</th>
              <th>Örnek</th>
              <th>Zorunlu</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>type</td>
              <td>Varlık türü</td>
              <td>stock, currency, gold, crypto</td>
              <td>✅</td>
            </tr>
            <tr>
              <td>symbol</td>
              <td>Sembol/Kod</td>
              <td>THYAO, USD, GAUTRY</td>
              <td>✅</td>
            </tr>
            <tr>
              <td>quantity</td>
              <td>Adet/Miktar</td>
              <td>100, 50, 10</td>
              <td>✅</td>
            </tr>
            <tr>
              <td>buyPrice</td>
              <td>Alış Fiyatı (TL)</td>
              <td>15.50, 30.00, 1800.00</td>
              <td>✅</td>
            </tr>
            <tr>
              <td>buyDate</td>
              <td>Alış Tarihi</td>
              <td>2024-01-15</td>
              <td>❌</td>
            </tr>
            <tr>
              <td>name</td>
              <td>Varlık Adı (opsiyonel)</td>
              <td>Türk Hava Yolları</td>
              <td>❌</td>
            </tr>
            <tr>
              <td>usdRate</td>
              <td>USD Kuru (opsiyonel)</td>
              <td>30.50</td>
              <td>❌</td>
            </tr>
          </tbody>
        </table>
      </div>

      {showPreview && (
        <div className="excel-preview-modal">
          <div className="excel-preview-content">
            <div className="excel-preview-header">
              <h4>📋 Önizleme ({previewData.length} satır)</h4>
              <button 
                onClick={() => setShowPreview(false)}
                className="close-button"
              >
                ✕
              </button>
            </div>

            {errors.length > 0 && (
              <div className="excel-errors">
                <h5>⚠️ Hatalar:</h5>
                <ul>
                  {errors.slice(0, 5).map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                  {errors.length > 5 && (
                    <li>... ve {errors.length - 5} adet daha hata</li>
                  )}
                </ul>
              </div>
            )}

            <div className="excel-preview-table-container">
              <table className="excel-preview-table">
                <thead>
                  <tr>
                    <th>Tür</th>
                    <th>Sembol</th>
                    <th>Adet</th>
                    <th>Alış Fiyatı</th>
                    <th>Tarih</th>
                    <th>Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.slice(0, 10).map((row, index) => {
                    const { valid } = validateRow(row, index);
                    return (
                      <tr key={index} className={valid ? 'valid' : 'invalid'}>
                        <td>{row.type}</td>
                        <td>{row.symbol}</td>
                        <td>{row.quantity}</td>
                        <td>₺{row.buyPrice}</td>
                        <td>{row.buyDate || '-'}</td>
                        <td>
                          {valid ? (
                            <span className="status-valid">✅</span>
                          ) : (
                            <span className="status-invalid">❌</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {previewData.length > 10 && (
                <p className="excel-more-rows">
                  ... ve {previewData.length - 10} satır daha
                </p>
              )}
            </div>

            <div className="excel-preview-actions">
              <button 
                onClick={() => setShowPreview(false)}
                className="cancel-button"
              >
                İptal
              </button>
              <button 
                onClick={handleConfirmImport}
                className="confirm-button"
                disabled={errors.length > 0 && errors.length === previewData.length}
              >
                ✅ {Math.max(0, previewData.length - errors.length)} Satırı İçe Aktar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExcelUpload;
